import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, UserCheck, AlertCircle, BookOpen, Newspaper,
  ClipboardList, ScanEye, Settings, Shield, Plus, Trash2,
  ChevronDown, ChevronUp, Users, Lock, Save, Loader2, BarChart2, Sunrise,
  TrendingUp, BookOpenCheck, Calendar, CalendarDays, MessageSquare, Sparkles, FileText,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { rolesAPI } from '../services/api';

// ── Module definitions ────────────────────────────────────
const MODULES = [
  { key: 'dashboard',           label: 'Dashboard',            icon: LayoutDashboard, alwaysView: true },
  { key: 'attendance',          label: 'Employee Attendance',  icon: UserCheck },
  { key: 'grievances',          label: 'Grievances',           icon: AlertCircle },
  { key: 'homework',            label: 'Homework',             icon: BookOpen },
  { key: 'newsletter',          label: 'Newsletter',           icon: Newspaper },
  { key: 'lesson_plan',         label: 'Toddle Lesson Plan',   icon: ClipboardList },
  { key: 'teacher_observation', label: 'Teacher Observations', icon: ScanEye },
  { key: 'learning_trackers',   label: 'Learning Trackers',    icon: BarChart2 },
  { key: 'morning_activity',    label: 'Morning Activity',     icon: Sunrise },
  { key: 'performance_analytics', label: 'Performance Analytics', icon: TrendingUp },
  { key: 'toddle_planner',        label: 'Toddle Planner',        icon: BookOpenCheck },
  { key: 'timetable',             label: 'Timetable',             icon: Calendar },
  { key: 'leave_management',      label: 'Leave Management',      icon: CalendarDays },
  { key: 'ptc',                   label: 'Parent Teacher Connect', icon: MessageSquare },
  { key: 'enrichment_programme',  label: 'Enrichment Programme',  icon: Sparkles },
  { key: 'reports',               label: 'Reports',               icon: FileText },
  { key: 'school_config',         label: 'School Configuration',  icon: Settings, adminOnly: true },
];

const SYSTEM_ROLES = ['admin', 'super_admin'];

const EMPTY_PERM = { can_view: false, can_add: false, can_edit: false, can_delete: false, can_approve: false, can_publish: false, can_export: false };

const PERM_COLS = [
  { field: 'can_view',    label: 'View',    title: 'Can view this module' },
  { field: 'can_add',     label: 'Add',     title: 'Can create new records' },
  { field: 'can_edit',    label: 'Edit',    title: 'Can edit existing records' },
  { field: 'can_delete',  label: 'Delete',  title: 'Can delete records' },
  { field: 'can_approve', label: 'Approve', title: 'Can approve records' },
  { field: 'can_publish', label: 'Publish', title: 'Can publish records' },
  { field: 'can_export',  label: 'Export',  title: 'Can export data' },
];

function buildPermState(permissions) {
  const state = {};
  MODULES.forEach(m => { state[m.key] = { ...EMPTY_PERM }; });
  if (permissions && Array.isArray(permissions)) {
    permissions.forEach(p => {
      if (state[p.module] !== undefined) {
        state[p.module] = {
          can_view:    !!p.can_view,
          can_add:     !!p.can_add,
          can_edit:    !!p.can_edit,
          can_delete:  !!p.can_delete,
          can_approve: !!p.can_approve,
          can_publish: !!p.can_publish,
          can_export:  !!p.can_export,
        };
      }
    });
  }
  state['dashboard'] = { ...state['dashboard'], can_view: true };
  return state;
}

function permStateToArray(permState) {
  return MODULES.map(m => ({
    module:      m.key,
    can_view:    !!permState[m.key]?.can_view,
    can_add:     !!permState[m.key]?.can_add,
    can_edit:    !!permState[m.key]?.can_edit,
    can_delete:  !!permState[m.key]?.can_delete,
    can_approve: !!permState[m.key]?.can_approve,
    can_publish: !!permState[m.key]?.can_publish,
    can_export:  !!permState[m.key]?.can_export,
  }));
}

// ── Left panel: Role card ─────────────────────────────────
function RoleCard({ role, selected, onClick }) {
  const isSystem = SYSTEM_ROLES.includes(role.name);
  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left px-4 py-3 rounded-xl border transition-all duration-150
        ${selected
          ? 'bg-primary-50 border-primary-300 shadow-sm'
          : 'bg-white border-slate-200 hover:border-primary-200 hover:bg-primary-50/40'
        }
      `}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-slate-800 capitalize truncate">{role.name}</span>
            {isSystem && (
              <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                System
              </span>
            )}
          </div>
          {role.description && (
            <p className="text-xs text-slate-500 mt-0.5 truncate">{role.description}</p>
          )}
        </div>
        <span className="flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
          {role.user_count ?? 0}
        </span>
      </div>
    </button>
  );
}

// ── Checkbox cell ─────────────────────────────────────────
function PermCheckbox({ checked, disabled, onChange }) {
  return (
    <label className={`flex items-center justify-center ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={e => onChange && onChange(e.target.checked)}
        className="w-4 h-4 rounded accent-primary-600"
      />
    </label>
  );
}

// ── Main component ────────────────────────────────────────
export default function RoleManagement() {
  const [roles, setRoles]           = useState([]);
  const [selected, setSelected]     = useState(null);
  const [permState, setPermState]   = useState({});
  const [nameState, setNameState]   = useState('');
  const [descState, setDescState]   = useState('');
  const [roleUsers, setRoleUsers]   = useState([]);
  const [showUsers, setShowUsers]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const [loading, setLoading]       = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showNewForm, setShowNewForm]   = useState(false);
  const [newName, setNewName]           = useState('');
  const [newDesc, setNewDesc]           = useState('');
  const [creating, setCreating]         = useState(false);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rolesAPI.getAll();
      setRoles(res.data);
    } catch {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const handleSelect = useCallback(async (role) => {
    setSelected(role);
    setNameState(role.name);
    setDescState(role.description || '');
    setShowUsers(false);
    setRoleUsers([]);

    const isSystem = SYSTEM_ROLES.includes(role.name);
    if (isSystem) {
      const state = {};
      MODULES.forEach(m => { state[m.key] = { can_view: true, can_add: true, can_edit: true, can_delete: true, can_approve: true, can_publish: true, can_export: true }; });
      setPermState(state);
    } else {
      setPermState(buildPermState(role.permissions));
    }
  }, []);

  const handleLoadUsers = useCallback(async () => {
    if (!selected) return;
    setUsersLoading(true);
    try {
      const res = await rolesAPI.getUsers(selected.id);
      setRoleUsers(res.data);
      setShowUsers(true);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [selected]);

  const handleSave = useCallback(async () => {
    if (!selected) return;
    setSaving(true);
    try {
      // update name/description (non-system roles only)
      const isSystem = SYSTEM_ROLES.includes(selected.name);
      if (!isSystem) {
        await rolesAPI.update(selected.id, { name: nameState, description: descState });
      }
      // save permissions — sends { permissions: [...] } so backend can destructure correctly
      await rolesAPI.setPermissions(selected.id, permStateToArray(permState));
      toast.success('Permissions saved — affected users must log out and back in to see changes', { duration: 5000 });
      // refresh roles list and rebuild permState from server response (keep UI in sync)
      const res = await rolesAPI.getAll();
      setRoles(res.data);
      const updated = res.data.find(r => r.id === selected.id);
      if (updated) {
        setSelected(updated);
        setPermState(buildPermState(updated.permissions)); // ← sync UI with saved state
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  }, [selected, nameState, descState, permState]);

  const handleDelete = useCallback(async () => {
    if (!selected) return;
    if (!window.confirm(`Delete role "${selected.name}"? This cannot be undone.`)) return;
    try {
      await rolesAPI.delete(selected.id);
      toast.success('Role deleted');
      setSelected(null);
      fetchRoles();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete role');
    }
  }, [selected, fetchRoles]);

  const handleCreate = useCallback(async () => {
    if (!newName.trim()) { toast.error('Role name is required'); return; }
    setCreating(true);
    try {
      await rolesAPI.create({ name: newName.trim(), description: newDesc.trim() });
      toast.success('Role created');
      setNewName('');
      setNewDesc('');
      setShowNewForm(false);
      fetchRoles();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create role');
    } finally {
      setCreating(false);
    }
  }, [newName, newDesc, fetchRoles]);

  const setPerm = (key, field, val) => {
    setPermState(prev => {
      const updated = { ...prev[key], [field]: val };
      // unchecking view clears all other permissions
      if (field === 'can_view' && !val) {
        Object.keys(updated).forEach(k => { updated[k] = false; });
      }
      // checking any action auto-enables view
      if (field !== 'can_view' && val) {
        updated.can_view = true;
      }
      return { ...prev, [key]: updated };
    });
  };

  const isSystem = selected ? SYSTEM_ROLES.includes(selected.name) : false;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  return (
    <div className="flex gap-4 min-h-[600px]">
      {/* ── Left panel: role list ── */}
      <div className="w-72 flex-shrink-0 flex flex-col gap-2">
        <div className="flex items-center gap-2 mb-1">
          <Shield size={16} className="text-primary-600" />
          <span className="text-sm font-semibold text-slate-700">Roles ({roles.length})</span>
        </div>

        <div className="flex flex-col gap-1.5 flex-1 overflow-y-auto pr-1">
          {roles.map(role => (
            <RoleCard
              key={role.id}
              role={role}
              selected={selected?.id === role.id}
              onClick={() => handleSelect(role)}
            />
          ))}
        </div>

        {/* New role form */}
        {showNewForm ? (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2 mt-2">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">New Role</p>
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Role name"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            />
            <input
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              placeholder="Description (optional)"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            />
            <div className="flex gap-2">
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
              >
                {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                Create
              </button>
              <button
                onClick={() => { setShowNewForm(false); setNewName(''); setNewDesc(''); }}
                className="flex-1 text-xs font-medium py-2 rounded-lg bg-slate-200 text-slate-600 hover:bg-slate-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowNewForm(true)}
            className="mt-2 flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border-2 border-dashed border-primary-200 text-primary-600 hover:bg-primary-50 text-sm font-medium transition-colors"
          >
            <Plus size={16} /> New Role
          </button>
        )}
      </div>

      {/* ── Right panel: permissions editor ── */}
      <div className="flex-1 min-w-0">
        {!selected ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-20 text-slate-400">
            <Shield size={48} className="mb-3 opacity-30" />
            <p className="font-medium">Select a role to manage permissions</p>
            <p className="text-sm mt-1">Choose from the list on the left</p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Role name / description */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                  <Shield size={16} className="text-primary-600" />
                  Role Details
                </h3>
                {isSystem && (
                  <span className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                    <Lock size={11} /> System role — name is locked
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1 uppercase tracking-wide">Role Name</label>
                  <input
                    value={nameState}
                    onChange={e => setNameState(e.target.value)}
                    disabled={isSystem}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white disabled:bg-slate-50 disabled:text-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1 uppercase tracking-wide">Description</label>
                  <input
                    value={descState}
                    onChange={e => setDescState(e.target.value)}
                    disabled={isSystem}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white disabled:bg-slate-50 disabled:text-slate-400"
                  />
                </div>
              </div>
            </div>

            {/* Module permissions */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Settings size={16} className="text-primary-600" />
                Module Permissions
                {isSystem && (
                  <span className="ml-auto flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                    <Lock size={11} /> Full access (cannot be changed)
                  </span>
                )}
              </h3>

              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm min-w-[680px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Module</th>
                      {PERM_COLS.map(col => (
                        <th key={col.field} title={col.title} className="w-16 text-center px-2 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {MODULES.map(mod => {
                      const Icon = mod.icon;
                      const perm = permState[mod.key] || { ...EMPTY_PERM };
                      const rowLocked = mod.adminOnly && !isSystem;

                      return (
                        <tr
                          key={mod.key}
                          className={`transition-colors ${rowLocked ? 'opacity-50 bg-slate-50/50' : 'hover:bg-slate-50/60'}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <Icon size={15} className="text-slate-400 flex-shrink-0" />
                              <span className="font-medium text-slate-700">{mod.label}</span>
                              {mod.alwaysView && (
                                <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">Always on</span>
                              )}
                              {mod.adminOnly && (
                                <span className="text-[10px] text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                  <Lock size={9} /> Admin only
                                </span>
                              )}
                            </div>
                          </td>
                          {PERM_COLS.map(col => {
                            const locked = isSystem || rowLocked || (col.field === 'can_view' && (mod.alwaysView || mod.adminOnly));
                            return (
                              <td key={col.field} className="px-2 py-3">
                                <PermCheckbox
                                  checked={isSystem || !!perm[col.field]}
                                  disabled={locked}
                                  onChange={val => !locked && setPerm(mod.key, col.field, val)}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-3 mt-4">
                <button
                  onClick={handleSave}
                  disabled={saving || isSystem}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 transition-colors shadow-sm"
                >
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  Save Permissions
                </button>
                {isSystem && (
                  <p className="text-xs text-slate-400">System roles cannot be modified</p>
                )}
              </div>
            </div>

            {/* Users in this role */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <button
                onClick={() => {
                  if (!showUsers) {
                    handleLoadUsers();
                  } else {
                    setShowUsers(false);
                  }
                }}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Users size={15} className="text-primary-600" />
                  Users in this Role
                  <span className="text-xs font-medium px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                    {selected.user_count ?? 0}
                  </span>
                </span>
                {usersLoading
                  ? <Loader2 size={15} className="animate-spin text-slate-400" />
                  : showUsers
                    ? <ChevronUp size={15} className="text-slate-400" />
                    : <ChevronDown size={15} className="text-slate-400" />
                }
              </button>

              {showUsers && (
                <div className="border-t border-slate-100">
                  {roleUsers.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-6">No users assigned to this role</p>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {roleUsers.map(u => (
                        <div key={u.id} className="flex items-center gap-3 px-5 py-3">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-primary-700">
                              {(u.name || u.email || '?')[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700">{u.name || '—'}</p>
                            <p className="text-xs text-slate-400">{u.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Delete role */}
            {!isSystem && (
              <div className="flex justify-end">
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 transition-colors"
                >
                  <Trash2 size={14} />
                  Delete Role
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
